import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';
import { AdminRole } from '../../constants/adminRoles';

export interface Admin {
  id: string;
  fullName: string;
  username: string;
  phoneNumber: string;
  email: string | null;
  role: AdminRole;
  status: 'active' | 'inactive';
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  admin: Admin;
  accessToken: string;
  refreshToken: string;
}

export interface UpdateProfileData {
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  newPassword?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const res = await apiClient.post<ApiEnvelope<LoginResponse>>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials
    );
    const data = res.data.data;

    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('admin_data', JSON.stringify(data.admin));
    }

    return data;
  }

  async getMe(): Promise<Admin> {
    const res = await apiClient.get<ApiEnvelope<Admin>>(
      API_ENDPOINTS.AUTH.ME
    );
    const admin = res.data.data;
    // ⭐ Refresh stored admin_data so a stale or missing cache
    //    is repaired on any successful /auth/me call.
    try {
      localStorage.setItem('admin_data', JSON.stringify(admin));
    } catch {
      /* ignore */
    }
    return admin;
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch {
      // ignore
    } finally {
      this.clear();
    }
  }

  getCurrentAdmin(): Admin | null {
    const raw = localStorage.getItem('admin_data');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken() && !!this.getCurrentAdmin();
  }

  async updateProfile(data: UpdateProfileData): Promise<Admin> {
    const res = await apiClient.patch<ApiEnvelope<Admin>>(
      API_ENDPOINTS.AUTH.UPDATE_PROFILE,
      data
    );
    const admin = res.data.data;
    localStorage.setItem('admin_data', JSON.stringify(admin));
    return admin;
  }

  clear(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('admin_data');
  }
}

const authService = new AuthService();
export default authService;